import React from "react";
import { Form } from "react-crud-form";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { Input } from "ui/components/input";
import EmployeeInput from "components/employee/input";
import { useNavigate } from "react-router-dom";
import SelectStatus from "components/select/status";
import { departmentSchema } from "./schema";
import { FormActions } from "components/form/actions";
import { useDepartmentForm } from "./useDepartmentForm";
import { PermissionControlledInput } from "permissions/components/input";
import EmployeeMultiSelectSheet from "components/employee/multiselect-sheet";
import { FormLabel } from "components/form";

const DepartmentForm = ({ departmentId }) => {
  const navigate = useNavigate();
  const { form, handleCreate, handleUpdate, handleRead } = useDepartmentForm({
    departmentId,
  });

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={departmentId || "+"}
      schema={departmentSchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => (
        <div className="space-y-6">
          {/* Name Field */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="departments"
                  type="form"
                >
                  <FormLabel required>Name</FormLabel>
                  <Input {...field} />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Status */}
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="departments"
                  type="form"
                >
                  <FormLabel>Status</FormLabel>
                  <SelectStatus
                    collection="departments"
                    onValueChange={(val) => field.onChange(val)}
                    {...field}
                  />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="head"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="departments"
                  type="form"
                >
                  <FormLabel required>Head</FormLabel>
                  <EmployeeInput
                    {...field}
                    filters={{ user: { role: { is_department_head: { _eq: true } } } }}
                  />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="coordinators"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="departments"
                  type="form"
                >
                  <FormLabel required>Coordinators</FormLabel>
                  <EmployeeMultiSelectSheet
                    value={field.value}
                    onChange={val => field.onChange(val)}
                    placeholder="Select Coordinators"
                    filter={{ user: { role: { is_department_coordinator: { _eq: true } } } }}
                    info="Select multiple coordinators to add to this department"
                  />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormActions
            isNewItem={isNewItem}
            isCreating={isCreating}
            isUpdating={isUpdating}
            isDirty={isDirty}
            createItem={() => createItem().then(() => navigate(-1))}
            updateItem={() => updateItem().then(() => navigate(-1))}
            showCancel={false}
            collection="departments"
            rule={isNewItem ? "create:*" : "update:*"}
          />
        </div>
      )}
    </Form>
  );
};

export default DepartmentForm;
